import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded'
import PaymentRoundedIcon from '@mui/icons-material/PaymentRounded'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import {
  Button,
  Drawer,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Typography,
  IconButton,
} from '@mui/material'
import { useState } from 'react'
import PaymentConfigForm from './PaymentConfigForm'

export default function PaymentConfigMenu({
  payoutEntityId,
  payoutEntityType,
  showPaymentConfigButtons = true,
  sx = {},
  entityName,
  onPaymentConfigSaved,
}) {
  const [anchorEl, setAnchorEl] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedConfig, setSelectedConfig] = useState(null)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const menuOpen = Boolean(anchorEl)

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleEntityConfig = () => {
    setSelectedConfig({
      entityId: payoutEntityId,
      entityType: payoutEntityType,
    })
    setDrawerOpen(true)
    handleMenuClose()
  }

  const handleSourceConfig = () => {
    setSelectedConfig({ entityId: 'AUMHUM', entityType: 'company' })
    setDrawerOpen(true)
    handleMenuClose()
  }

  const handleDrawerClose = () => {
    setDrawerOpen(false)
    setSelectedConfig(null)
  }

  const handleFormSaved = (savedConfig) => {
    if (onPaymentConfigSaved) {
      onPaymentConfigSaved(savedConfig)
    }
    handleDrawerClose()
  }

  const getDrawerTitle = () => {
    if (!selectedConfig) return 'Payment configuration'
    if (selectedConfig.entityType === 'company') {
      return 'Source payment configuration'
    }
    return entityName
      ? entityName + ' — Payment configuration'
      : 'Payment configuration' +
          (selectedConfig.entityId ? ' (' + selectedConfig.entityId + ')' : '')
  }

  if (!showPaymentConfigButtons) {
    return null
  }

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        onClick={handleMenuClick}
        endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: 20 }} />}
        startIcon={<PaymentRoundedIcon sx={{ fontSize: 20 }} />}
        sx={{
          flexShrink: 0,
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          px: 2,
          py: 1,
          borderWidth: 1.5,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          '&:hover': {
            borderWidth: 1.5,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          },
          ...sx,
        }}
        aria-controls={menuOpen ? 'payment-config-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={menuOpen ? 'true' : undefined}
      >
        Payment config
      </Button>
      <Menu
        id="payment-config-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          elevation: 8,
          sx: {
            minWidth: 240,
            borderRadius: 2,
            mt: 1.5,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid',
            borderColor: theme.palette.divider,
            '& .MuiMenuItem-root': {
              py: 1.25,
              px: 2,
              fontSize: '0.9375rem',
            },
            '& .MuiListItemIcon-root': {
              minWidth: 40,
              color: 'primary.main',
            },
          },
        }}
      >
        <MenuItem onClick={handleEntityConfig}>
          <ListItemIcon>
            <PersonOutlineRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={entityName ? 'For this ' + entityName : 'For this entity'}
            primaryTypographyProps={{ fontWeight: 500 }}
          />
        </MenuItem>
        <Divider sx={{ my: 1 }} />
        <MenuItem onClick={handleSourceConfig}>
          <ListItemIcon>
            <BusinessRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Source (company)"
            secondary="AUMHUM"
            primaryTypographyProps={{ fontWeight: 500 }}
            secondaryTypographyProps={{ variant: 'caption' }}
          />
        </MenuItem>
      </Menu>

      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={drawerOpen}
        onClose={handleDrawerClose}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0,0,0,0.32)',
              backdropFilter: 'blur(4px)',
            },
          },
        }}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 'min(90vw, 640px)',
            maxWidth: isMobile ? '100%' : 640,
            height: isMobile ? '92vh' : '100%',
            borderTopLeftRadius: isMobile ? 16 : 0,
            borderBottomLeftRadius: isMobile ? 0 : 0,
            boxShadow: isMobile
              ? '0 -8px 32px rgba(0,0,0,0.16), 0 -2px 8px rgba(0,0,0,0.08)'
              : '-8px 0 32px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
          },
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            backgroundColor: theme.palette.background.default,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: theme.spacing(2, 2.5),
              borderBottom: `1px solid ${theme.palette.divider}`,
              background:
                theme.palette.mode === 'light'
                  ? `linear-gradient(180deg, ${theme.palette.grey[50]} 0%, ${theme.palette.background.paper} 100%)`
                  : theme.palette.background.paper,
              flexShrink: 0,
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              color="text.primary"
              sx={{ fontSize: '1.125rem', letterSpacing: '-0.01em' }}
            >
              {getDrawerTitle()}
            </Typography>
            <IconButton
              onClick={handleDrawerClose}
              size="small"
              aria-label="Close"
              sx={{
                color: theme.palette.text.secondary,
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  color: theme.palette.text.primary,
                },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </div>

          <div
            style={{
              flex: 1,
              overflow: 'auto',
              padding: theme.spacing(isMobile ? 2 : 3),
            }}
          >
            {selectedConfig && (
              <PaymentConfigForm
                hideEntitySection
                inDialog={false}
                entityId={selectedConfig.entityId}
                entityType={selectedConfig.entityType}
                onClose={handleDrawerClose}
                onSaved={handleFormSaved}
              />
            )}
          </div>
        </div>
      </Drawer>
    </>
  )
}
